import { useState, useEffect } from 'react';
import api from '@/api';
import { Loader2, Truck, Search } from 'lucide-react';
import { toast } from 'sonner';
import FilterBar, { FilterSelect, FilterInput } from '@/components/FilterBar';

const EMPTY = { status:'', unit_search:'', make:'', unit_type:'' };

export default function PortalUnits() {
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY);

  const activeCount = Object.values(filters).filter(v => v !== '').length;

  useEffect(() => { load(); }, [filters]);

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([_,v]) => v !== ''));
      const res = await api.get('/trucks', { params });
      setTrucks(res.data);
    } catch { toast.error('Failed to load units'); }
    finally { setLoading(false); }
  };

  const setF = (key, val) => setFilters(p => ({ ...p, [key]: val }));

  const truckCount = trucks.filter(t => t.unit_type === 'Truck' || !t.unit_type).length;
  const trailerCount = trucks.filter(t => t.unit_type === 'Trailer').length;

  const TRUCK_STATUS_COLORS = {
    active:'text-[var(--success)] border-[var(--success)] bg-[var(--success-muted)]',
    repair_in_progress:'text-[var(--warning)] border-[var(--warning)] bg-[var(--warning-muted)]',
    inactive:'text-[var(--text-muted)] border-[var(--border)] bg-[var(--surface)]',
  };

  return (
    <div className="space-y-5 animate-fade-in" data-testid="portal-units-page">
      <div>
        <h1 className="font-['Manrope'] font-bold text-2xl" style={{ color: 'var(--text-primary)' }}>My Units</h1>
        <p className="font-['IBM_Plex_Sans'] text-sm mt-1" style={{ color: 'var(--text-muted)' }}>{trucks.length} units in your fleet</p>
      </div>

      <FilterBar onClear={() => setFilters(EMPTY)} activeCount={activeCount}>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-faint)' }} />
            <input value={filters.unit_search} onChange={e => setF('unit_search', e.target.value)}
              placeholder="Search unit #, VIN, plate..."
              className="w-full input-slate pl-8" />
          </div>
          <FilterSelect label="Type" value={filters.unit_type} onChange={v => setF('unit_type', v)}
            options={[{value:'Truck',label:`Trucks (${truckCount})`},{value:'Trailer',label:`Trailers (${trailerCount})`}]} />
          <FilterSelect label="Status" value={filters.status} onChange={v => setF('status', v)}
            options={[{value:'active',label:'Active'},{value:'repair_in_progress',label:'Repair In Progress'},{value:'inactive',label:'Inactive'}]} />
          <FilterInput label="Make" value={filters.make} onChange={v => setF('make', v)} placeholder="Kenworth..." />
        </div>
      </FilterBar>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--primary)' }} /></div>
      ) : trucks.length === 0 ? (
        <div className="py-16 text-center rounded-md border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <Truck size={32} className="mx-auto mb-3" style={{ color: 'var(--border)' }} />
          <p className="font-['IBM_Plex_Sans']" style={{ color: 'var(--text-muted)' }}>No units match your filters</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {trucks.map(t => (
            <div key={t.id} className="p-5 rounded-md border transition-colors"
              style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--primary)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
              data-testid={`portal-unit-${t.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="font-['JetBrains_Mono'] text-lg font-bold" style={{ color: 'var(--primary)' }}>{t.unit_number}</span>
                  <span className="text-[9px] uppercase px-1.5 py-0.5 rounded-full font-['JetBrains_Mono']" style={{ background: 'var(--bg)', color: t.unit_type === 'Trailer' ? 'var(--warning)' : 'var(--primary)', border: '1px solid var(--border)' }}>
                    {t.unit_type || 'Truck'}
                  </span>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wide border rounded-full px-2.5 py-1 font-['IBM_Plex_Sans'] ${TRUCK_STATUS_COLORS[t.status]||TRUCK_STATUS_COLORS.active}`}>
                  {t.status?.replace(/_/g,' ')}
                </span>
              </div>
              <div className="space-y-1.5">
                <p className="text-sm font-semibold font-['IBM_Plex_Sans']" style={{ color: 'var(--text-primary)' }}>{t.year} {t.make} {t.model}</p>
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-['IBM_Plex_Sans']" style={{ color: 'var(--text-faint)' }}>Mileage</p>
                    <p className="text-sm font-['JetBrains_Mono']" style={{ color: 'var(--text-muted)' }}>{t.current_mileage?.toLocaleString()} mi</p>
                  </div>
                  {t.plate && (
                    <div>
                      <p className="text-[10px] uppercase tracking-wider font-['IBM_Plex_Sans']" style={{ color: 'var(--text-faint)' }}>Plate</p>
                      <p className="text-sm font-['JetBrains_Mono']" style={{ color: 'var(--text-muted)' }}>{t.plate}</p>
                    </div>
                  )}
                </div>
                {t.vin && <p className="text-[10px] font-['JetBrains_Mono']" style={{ color: 'var(--text-faint)' }}>VIN: ...{t.vin.slice(-6)}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
